import { Badge, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Role } from '../../types/enums/Role.enum';
import { Resource } from '../../types/Resources/Resource.type';
import { User } from '../../types/User/User.type.ts';
import ResourceTableItem from './ResourceTableItem';

interface ResourceTableProps {
  resources: Resource[];
  approvalCount: number;
  onEdit: (resource: Resource) => void;
  onPreview: (resource: Resource) => void;
  onDownload: (resource: Resource) => void;
  onFavoriteToggle: (resourceId: number) => void;
  onReview?: (resource: Resource) => void;
  onDelete: (resource: Resource) => void;
  fetchNextPage: () => void;
  loadingNextPage: boolean;
  hasNextPage: boolean;
  activeTab: number;
  onTabChange: (tab: number) => void;
  isLoading: boolean;
  userInfo?: User;
}

export function ResourceTable({
  resources,
  approvalCount,
  onEdit,
  onPreview,
  onDownload,
  onFavoriteToggle,
  onReview,
  onDelete,
  fetchNextPage,
  loadingNextPage,
  hasNextPage,
  activeTab,
  onTabChange,
  isLoading,
  userInfo,
}: ResourceTableProps) {
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    onTabChange(newValue);
  };

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && fetchNextPage && !loadingNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, loadingNextPage]);

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          marginY: '32px',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '50px',
            color: '#718096',
            border: '1px solid transparent',
            '&.Mui-selected': {
              color: '#fff',
              backgroundColor: 'primary.main',
            },
          },
        }}
      >
        <Tab label='All Resources' disableRipple />
        <Tab label='Favorites' disableRipple />
        <Tab
          disableRipple
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Pending requests
              {approvalCount > 0 && (
                <Badge
                  badgeContent={approvalCount}
                  color='primary'
                  sx={{
                    '& .MuiBadge-badge': {
                      position: 'relative',
                      transform: 'none',
                      fontSize: '11px',
                      borderRadius: '10px',
                      paddingX: '10px',
                      backgroundColor: activeTab === 2 ? '#fff' : 'primary.main',
                      color: activeTab === 2 ? 'primary.main' : '#fff',
                    },
                  }}
                />
              )}
            </Box>
          }
        />
        {userInfo?.role.id === Role.SUPER_ADMIN && <Tab label='Deleted Resources' disableRipple />}
      </Tabs>
      <Box sx={{ marginTop: 2, overflowY: 'auto', maxHeight: '620px' }}>
        {isLoading ? (
          <Box
            sx={{
              padding: 6,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : resources.length === 0 ? (
          <Box
            sx={{
              padding: 6,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant='body2'>No resources found</Typography>
          </Box>
        ) : (
          <>
            {resources.map((resource) => (
              <ResourceTableItem
                key={resource.id}
                resource={resource}
                onEdit={onEdit}
                onPreview={onPreview}
                onDownload={onDownload}
                onFavoriteToggle={onFavoriteToggle}
                onReview={onReview}
                onDelete={onDelete}
              />
            ))}
            {hasNextPage && <div ref={ref} style={{ height: '1px', width: '100%' }}></div>}
          </>
        )}

        {loadingNextPage && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <Typography
              variant='caption'
              sx={{
                color: '#a0aec0',
              }}
            >
              Loading more cases...
            </Typography>
          </div>
        )}
      </Box>
    </Box>
  );
}

export default ResourceTable;
